import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useAccess } from '../hooks/useAccess';
import supplierService, { Supplier } from '../services/supplierService';
import SuppliersTab from '../components/suppliers/SuppliersTab';
import InvoicesTab from '../components/suppliers/InvoicesTab';

/**
 * تأمین‌کنندگان و فاکتورهای خرید
 *  - تب ۱: شرکت‌های پخش/تولیدکننده + جمع خرید هر کدام
 *  - تب ۲: فاکتورهای خرید + نوار خلاصه (خرید، فروش بالقوه، سود، اقلام)
 */
const SupplierManagement: React.FC = () => {
  const { has } = useAccess();
  const canReadSuppliers = has('admin_supplier_read');
  const canReadInvoices = has('admin_purchase_invoice_read');
  const canWriteSuppliers = has('admin_supplier_write');
  const canWriteInvoices = has('admin_purchase_invoice_write');

  const [tab, setTab] = useState<0 | 1>(canReadSuppliers || !canReadInvoices ? 0 : 1);
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  const loadSuppliers = useCallback(async () => {
    try {
      const res = await supplierService.listSuppliers({ limit: 500 });
      setSuppliers(res.items);
    } catch {
      setSuppliers([]);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers, reloadKey]);

  return (
    <Box dir="rtl" sx={{ p: { xs: 1.5, md: 3 } }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
        تأمین‌کنندگان و فاکتورهای خرید
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        شرکت‌های پخش و تولیدکننده، فاکتورهای خرید کالا، بهای تمام‌شده و سود بالقوه موجودی
      </Typography>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab icon={<LocalShippingIcon />} iconPosition="start" label="تأمین‌کنندگان" value={0} disabled={!canReadSuppliers} />
          <Tab icon={<ReceiptLongIcon />} iconPosition="start" label="فاکتورهای خرید" value={1} disabled={!canReadInvoices} />
        </Tabs>
      </Paper>

      {tab === 0 ? (
        <SuppliersTab
          canWrite={canWriteSuppliers}
          reloadKey={reloadKey}
          onShowInvoices={(id) => {
            setSupplierFilter(id);
            setTab(1);
          }}
        />
      ) : (
        <InvoicesTab
          suppliers={suppliers}
          supplierFilter={supplierFilter}
          onSupplierFilterChange={setSupplierFilter}
          canWrite={canWriteInvoices}
          onInvoiceCreated={() => setReloadKey((k) => k + 1)}
        />
      )}
    </Box>
  );
};

export default SupplierManagement;
